import React, { useState, useEffect, useCallback } from 'react';
import { UploadCloud, FileText, Link, Play, Save, AlertTriangle, Check, Trash2, Edit2, Type, Calendar, Hash, ToggleLeft, ToggleRight, X } from 'lucide-react';
import { Dataset, Column } from '../types';
import Papa from 'papaparse';

interface DataLoaderPageProps {
    onSaveDataset: (dataset: Dataset) => void;
    existingDatasets: Dataset[];
    initialSourceType?: SourceType;
}

type SourceType = 'file' | 'paste' | 'url';
type DetectedFormat = 'json' | 'csv' | 'logs' | 'text' | null;

// --- Helper Functions ---

const detectFormat = (rawInput: string): DetectedFormat => {
    const trimmed = rawInput.trim();
    if (!trimmed) return null;

    // JSON Detection
    if ((trimmed.startsWith('{') || trimmed.startsWith('[')) && (trimmed.endsWith('}') || trimmed.endsWith(']'))) {
        try {
            JSON.parse(trimmed);
            return 'json';
        } catch (e) {
            // Not valid JSON, fall through
        }
    }

    // CSV Detection
    const lines = trimmed.split('\n').filter(l => l.trim());
    if (lines.length > 0) {
        const firstLine = lines[0];
        if (firstLine.includes(',') || firstLine.includes(';') || firstLine.includes('\t')) {
            // Simple check: if delimiters exist and we have multiple lines or multiple columns
            return 'csv';
        }
    }

    // Logs Detection (key=value)
    if (lines.some(line => line.includes('=') && line.split('=').length > 1)) {
        return 'logs';
    }

    return 'text';
};

const flattenJSON = (obj: any, prefix = '', res: any = {}) => {
    for (const key in obj) {
        if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;
        const val = obj[key];
        const newKey = prefix ? `${prefix}.${key}` : key;
        if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
            flattenJSON(val, newKey, res);
        } else {
            res[newKey] = val;
        }
    }
    return res;
};

const parseJSON = (raw: string): any[] => {
    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
            return parsed.map(item => typeof item === 'object' && item !== null ? flattenJSON(item) : { value: item });
        } else if (typeof parsed === 'object' && parsed !== null) {
            // Check if this object contains an array property (common in REST APIs like randomuser.me)
            // Look for the first property that is an array of objects
            for (const key in parsed) {
                if (Array.isArray(parsed[key]) && parsed[key].length > 0) {
                    const firstItem = parsed[key][0];
                    // If the array contains objects, extract it as the data source
                    if (typeof firstItem === 'object' && firstItem !== null && !Array.isArray(firstItem)) {
                        console.log(`[parseJSON] Detected nested array in '${key}' property, extracting ${parsed[key].length} items`);
                        return parsed[key].map((item: any) =>
                            typeof item === 'object' && item !== null ? flattenJSON(item) : { value: item }
                        );
                    }
                }
            }
            // If no suitable array found, flatten the object as a single row
            return [flattenJSON(parsed)];
        } else {
            return [];
        }
    } catch (e) {
        throw new Error("Invalid JSON input. Please fix the syntax and try again.");
    }
};

const parseCSV = (raw: string): Promise<any[]> => {
    return new Promise((resolve, reject) => {
        Papa.parse(raw, {
            header: true,
            skipEmptyLines: true,
            dynamicTyping: true,
            complete: (results) => {
                if (results.errors.length > 0) {
                    // If header parsing fails, try without header
                    Papa.parse(raw, {
                        header: false,
                        skipEmptyLines: true,
                        dynamicTyping: true,
                        complete: (resNoHeader) => {
                            if (resNoHeader.errors.length) {
                                reject(new Error("Failed to parse CSV."));
                            } else {
                                // Generate headers Column 1, Column 2...
                                const data = (resNoHeader.data as any[]).map((row: any[]) => {
                                    const obj: any = {};
                                    row.forEach((val, idx) => {
                                        obj[`Column ${idx + 1}`] = val;
                                    });
                                    return obj;
                                });
                                resolve(data);
                            }
                        }
                    });
                } else {
                    resolve(results.data as any[]);
                }
            },
            error: (err) => reject(err)
        });
    });
};

const parseLogs = (raw: string): any[] => {
    const lines = raw.split('\n').filter(l => l.trim());
    return lines.map(line => {
        const obj: any = {};
        // Split by whitespace or specific log delimiters if needed. 
        // For now, assuming key=value pairs separated by space or just key=value
        // A simple regex for key=value
        const matches = line.matchAll(/([a-zA-Z0-9_.-]+)=([^ ]+)/g);
        for (const match of matches) {
            obj[match[1]] = match[2];
        }
        // If no matches, maybe put whole line in "raw"
        if (Object.keys(obj).length === 0) {
            obj['raw'] = line;
        }
        return obj;
    });
};

const cleanColumnNames = (columns: string[]): string[] => {
    const seen = new Set<string>();
    return columns.map(col => {
        let name = col;
        // Remove table prefixes (e.g. "table.col" -> "col")
        if (name.includes('.')) {
            const parts = name.split('.');
            name = parts[parts.length - 1];
        }
        // Replace underscores with spaces
        name = name.replace(/_/g, ' ');
        // Title Case
        name = name.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());

        // Ensure uniqueness
        let uniqueName = name;
        let counter = 1;
        while (seen.has(uniqueName)) {
            uniqueName = `${name} ${counter}`;
            counter++;
        }
        seen.add(uniqueName);
        return uniqueName;
    });
};

const inferDataTypes = (rows: any[], columns: string[]): Column[] => {
    return columns.map(col => {
        // Check first 50 non-null values
        const sampleValues = rows.slice(0, 50).map(r => r[col]).filter(v => v !== null && v !== undefined && v !== '');

        if (sampleValues.length === 0) return { name: col, type: 'string' };

        const isNumber = sampleValues.every(v => !isNaN(Number(v)));
        if (isNumber) {
            // Check if integer
            const isInteger = sampleValues.every(v => Number.isInteger(Number(v)));
            // We map both to 'number' for now as our Column type is simple, but we could store metadata
            return { name: col, type: 'number' };
        }

        const isBoolean = sampleValues.every(v => {
            const s = String(v).toLowerCase();
            return s === 'true' || s === 'false';
        });
        if (isBoolean) return { name: col, type: 'boolean' };

        const isDate = sampleValues.every(v => !isNaN(Date.parse(String(v))));
        if (isDate) return { name: col, type: 'date' };

        return { name: col, type: 'string' };
    });
};


const DataLoaderPage: React.FC<DataLoaderPageProps> = ({ onSaveDataset, existingDatasets, initialSourceType }) => {
    const [sourceType, setSourceType] = useState<SourceType>(initialSourceType || 'paste');
    const [rawInput, setRawInput] = useState('');
    const [urlInput, setUrlInput] = useState('');
    const [detectedFormat, setDetectedFormat] = useState<DetectedFormat>(null);
    const [parsedData, setParsedData] = useState<any[]>([]);
    const [originalColumns, setOriginalColumns] = useState<string[]>([]);
    const [cleanedColumns, setCleanedColumns] = useState<Column[]>([]);
    const [datasetName, setDatasetName] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [autoDetectTypes, setAutoDetectTypes] = useState(true);

    useEffect(() => {
        if (initialSourceType) {
            setSourceType(initialSourceType);
        }
    }, [initialSourceType]);

    const handleDetectAndParse = async () => {
        setLoading(true);
        setError(null);
        setParsedData([]);

        let inputToParse = rawInput;

        if (sourceType === 'url') {
            if (!urlInput) {
                setError("Please enter a URL.");
                setLoading(false);
                return;
            }
            try {
                const res = await fetch(urlInput);
                const text = await res.text();
                inputToParse = text;
                setRawInput(text); // Show fetched content
            } catch (e) {
                setError("Failed to fetch from URL.");
                setLoading(false);
                return;
            }
        }

        if (!inputToParse.trim()) {
            setError("No input data provided.");
            setLoading(false);
            return;
        }

        const format = detectFormat(inputToParse);
        setDetectedFormat(format);

        try {
            let data: any[] = [];
            if (format === 'json') {
                data = parseJSON(inputToParse);
            } else if (format === 'csv') {
                data = await parseCSV(inputToParse);
            } else if (format === 'logs') {
                data = parseLogs(inputToParse);
            } else {
                // Fallback text parsing? For now treat as single column
                data = inputToParse.split('\n').filter(l => l.trim()).map(l => ({ raw: l }));
            }

            if (data.length === 0) {
                setError("No rows found in data.");
            } else {
                setParsedData(data);

                // Extract columns from first row (or union of all keys if needed, but first row is standard)
                // For JSON/Logs with varying keys, union is better.
                const allKeys = new Set<string>();
                data.slice(0, 100).forEach(row => Object.keys(row).forEach(k => allKeys.add(k)));
                const cols = Array.from(allKeys);

                setOriginalColumns(cols);

                const cleanedNames = cleanColumnNames(cols);

                // Map original keys to cleaned names for the data transformation later
                // But for now, we just store the schema. 
                // We need to actually rename the keys in the data or keep a mapping.
                // Let's keep a mapping in the Column definition if possible, or just rename now.
                // The requirements say "clean schema is saved", implying we transform the data.

                // Let's create the Column objects
                const inferred = inferDataTypes(data, cols);

                // Apply cleaned names
                const finalColumns = inferred.map((col, idx) => ({
                    ...col,
                    name: cleanedNames[idx] // This is the display name / new key
                }));

                setCleanedColumns(finalColumns);

                // Auto-generate name
                if (!datasetName) {
                    const dateStr = new Date().toLocaleDateString();
                    setDatasetName(`${format ? format.toUpperCase() : 'Imported'} Dataset (${dateStr})`);
                }
            }
        } catch (e: any) {
            setError(e.message || "Failed to parse data.");
        } finally {
            setLoading(false);
        }
    };

    const handleSave = () => {
        if (parsedData.length === 0) return;

        // Transform data to match cleaned column names
        const transformedData = parsedData.map(row => {
            const newRow: any = {};
            originalColumns.forEach((origCol, idx) => {
                const newCol = cleanedColumns[idx].name;
                newRow[newCol] = row[origCol];
            });
            return newRow;
        });

        const newDataset: Dataset = {
            id: `ds-${Date.now()}`,
            name: datasetName || 'Untitled Dataset',
            data: transformedData,
            schema: cleanedColumns,
            sampleRows: transformedData.slice(0, 100),
            createdAt: new Date().toISOString(),
            ownerId: 'local', // Or pass user ID if available, defaulting to local for now
        };

        onSaveDataset(newDataset);
    };

    const [dragActive, setDragActive] = useState(false);

    const processFile = (file: File) => {
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            if (event.target?.result) {
                setRawInput(event.target.result as string);
                setSourceType('file'); // Switch to file mode visually if needed, or just populate rawInput

                // Auto-populate dataset name from filename (remove extension)
                const nameWithoutExt = file.name.split('.').slice(0, -1).join('.');
                setDatasetName(nameWithoutExt);
            }
        };
        reader.readAsText(file);
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) processFile(file);
    };

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!dragActive) setDragActive(true);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        const file = e.dataTransfer.files?.[0];
        if (file) processFile(file);
    };

    return (
        <div className="flex h-full gap-4 p-4">
            {/* LEFT PANEL: Source Selection */}
            <div className="w-1/4 glass-panel rounded-xl p-4 flex flex-col gap-4">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <UploadCloud className="w-5 h-5 text-neon-gold" /> Source
                </h3>
                <p className="text-xs text-gray-400">Import data from files, paste, or URLs. Clean it, preview it, and save as a dataset.</p>

                <div className="flex flex-col gap-2">
                    <button
                        onClick={() => setSourceType('file')}
                        className={`p-3 rounded-lg flex items-center gap-3 transition-all ${sourceType === 'file' ? 'bg-neon-gold/20 border border-neon-gold text-white' : 'bg-white/5 hover:bg-white/10 text-gray-400'}`}
                    >
                        <FileText className="w-4 h-4" /> Upload File
                    </button>
                    <button
                        onClick={() => setSourceType('paste')}
                        className={`p-3 rounded-lg flex items-center gap-3 transition-all ${sourceType === 'paste' ? 'bg-neon-gold/20 border border-neon-gold text-white' : 'bg-white/5 hover:bg-white/10 text-gray-400'}`}
                    >
                        <Edit2 className="w-4 h-4" /> Paste Raw Data
                    </button>
                    <button
                        onClick={() => setSourceType('url')}
                        className={`p-3 rounded-lg flex items-center gap-3 transition-all ${sourceType === 'url' ? 'bg-neon-gold/20 border border-neon-gold text-white' : 'bg-white/5 hover:bg-white/10 text-gray-400'}`}
                    >
                        <Link className="w-4 h-4" /> From URL
                    </button>
                </div>

                <div className="flex-1 flex flex-col gap-2 min-h-0">
                    {sourceType === 'file' && (
                        <div
                            onDragEnter={handleDragEnter}
                            onDragLeave={handleDragLeave}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            className={`border-2 border-dashed rounded-lg p-8 flex flex-col items-center justify-center text-center gap-2 transition-all duration-300 ${dragActive ? 'border-neon-gold bg-neon-gold/10 scale-[1.02]' : 'border-white/20 hover:border-neon-gold/50'}`}
                        >
                            <UploadCloud className={`w-8 h-8 transition-colors ${dragActive ? 'text-neon-gold animate-bounce' : 'text-gray-500'}`} />
                            <p className="text-sm text-gray-400">Drag & drop or click to upload</p>
                            <input type="file" className="hidden" id="file-upload" onChange={handleFileUpload} accept=".csv,.json,.txt,.tsv" />
                            <label htmlFor="file-upload" className="mt-2 px-4 py-2 bg-white/10 rounded-md text-sm cursor-pointer hover:bg-white/20">Browse Files</label>
                        </div>
                    )}

                    {(sourceType === 'paste' || sourceType === 'file') && (
                        <textarea
                            className="flex-1 bg-black/30 border border-white/10 rounded-lg p-3 text-xs font-mono text-gray-300 focus:border-neon-gold focus:outline-none resize-none"
                            placeholder="Paste your JSON, CSV, or Logs here..."
                            value={rawInput}
                            onChange={(e) => setRawInput(e.target.value)}
                        />
                    )}

                    {sourceType === 'url' && (
                        <div className="flex flex-col gap-2">
                            <input
                                type="text"
                                placeholder="https://api.example.com/data.json"
                                className="bg-black/30 border border-white/10 rounded-lg p-3 text-sm text-white focus:border-neon-gold focus:outline-none"
                                value={urlInput}
                                onChange={(e) => setUrlInput(e.target.value)}
                            />
                            <p className="text-xs text-gray-500">Supports JSON and CSV endpoints.</p>
                        </div>
                    )}
                </div>

                <button
                    onClick={handleDetectAndParse}
                    disabled={loading}
                    className="w-full py-3 bg-neon-gold text-black font-bold rounded-lg hover:bg-neon-gold/90 transition-all flex items-center justify-center gap-2"
                >
                    {loading ? <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> : <Play className="w-4 h-4" />}
                    Detect & Parse
                </button>

                {error && (
                    <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg flex items-start gap-2 text-red-200 text-sm">
                        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                        <span>{error}</span>
                    </div>
                )}
            </div>

            {/* CENTER PANEL: Preview */}
            <div className="flex-1 glass-panel rounded-xl p-4 flex flex-col gap-4 overflow-hidden">
                <div className="flex justify-between items-center">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        <Type className="w-5 h-5 text-neon-gold" /> Data Preview
                    </h3>
                    {detectedFormat && (
                        <span className="px-2 py-1 bg-white/10 rounded text-xs text-gray-300 uppercase font-mono">
                            Detected: {detectedFormat}
                        </span>
                    )}
                </div>

                <div className="flex-1 overflow-auto border border-white/10 rounded-lg bg-black/20 relative">
                    {parsedData.length === 0 ? (
                        <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500 gap-2">
                            <Type className="w-12 h-12 opacity-20" />
                            <p>Paste data, upload a file, or provide a URL to see a preview here.</p>
                        </div>
                    ) : (
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-white/5 sticky top-0 z-10 backdrop-blur-sm">
                                <tr>
                                    {cleanedColumns.map((col, idx) => (
                                        <th key={idx} className="p-3 text-xs font-semibold text-gray-300 border-b border-white/10 min-w-[150px]">
                                            <div className="flex flex-col gap-1">
                                                <input
                                                    type="text"
                                                    value={col.name}
                                                    onChange={(e) => {
                                                        const newCols = [...cleanedColumns];
                                                        newCols[idx].name = e.target.value;
                                                        setCleanedColumns(newCols);
                                                    }}
                                                    className="bg-transparent border-none focus:ring-0 text-white font-bold p-0 w-full"
                                                />
                                                <div className="flex items-center gap-1">
                                                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${col.type === 'number' ? 'bg-blue-500/20 text-blue-300' :
                                                        col.type === 'date' ? 'bg-green-500/20 text-green-300' :
                                                            col.type === 'boolean' ? 'bg-purple-500/20 text-purple-300' :
                                                                'bg-gray-500/20 text-gray-300'
                                                        }`}>
                                                        {col.type}
                                                    </span>
                                                </div>
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5">
                                {parsedData.slice(0, 50).map((row, rIdx) => (
                                    <tr key={rIdx} className="hover:bg-white/5 transition-colors">
                                        {originalColumns.map((origCol, cIdx) => (
                                            <td key={cIdx} className="p-3 text-xs text-gray-400 truncate max-w-[200px]">
                                                {typeof row[origCol] === 'object' ? JSON.stringify(row[origCol]) : String(row[origCol] ?? '')}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
                <div className="text-xs text-gray-500 flex justify-between">
                    <span>Showing first {Math.min(parsedData.length, 50)} rows</span>
                    <span>Total Rows: {parsedData.length}</span>
                </div>
            </div>

            {/* RIGHT PANEL: Options */}
            <div className="w-1/5 glass-panel rounded-xl p-4 flex flex-col gap-4">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <Save className="w-5 h-5 text-neon-gold" /> Options
                </h3>

                <div className="space-y-4">
                    <div className="space-y-2">
                        <label className="text-xs text-gray-400 uppercase font-bold">Dataset Name</label>
                        <input
                            type="text"
                            value={datasetName}
                            onChange={(e) => setDatasetName(e.target.value)}
                            placeholder="My Dataset"
                            className="w-full bg-black/30 border border-white/10 rounded-lg p-2 text-sm text-white focus:border-neon-gold focus:outline-none"
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-xs text-gray-400 uppercase font-bold">Settings</label>
                        <div className="flex items-center justify-between p-2 bg-white/5 rounded-lg">
                            <span className="text-sm text-gray-300">Auto-Detect Types</span>
                            <button onClick={() => setAutoDetectTypes(!autoDetectTypes)} className="text-neon-gold">
                                {autoDetectTypes ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5 text-gray-500" />}
                            </button>
                        </div>
                    </div>

                    <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-xs text-blue-200">
                        <p className="font-bold mb-1">Note:</p>
                        <p>Nested JSON objects will be automatically flattened. Original keys are preserved in the mapping.</p>
                    </div>
                </div>

                <div className="mt-auto">
                    <button
                        onClick={handleSave}
                        disabled={parsedData.length === 0}
                        className="w-full py-3 bg-neon-gold text-black font-bold rounded-lg hover:bg-neon-gold/90 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Save className="w-4 h-4" /> Save Dataset
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DataLoaderPage;
